import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Decoration, DecorationSet } from 'prosemirror-view';
import { Node as ProseMirrorNode } from 'prosemirror-model';
import { lexer } from 'marked';

export interface ChangeIndicatorOptions {
  previousContent?: string;
  currentContent?: string;
  enabled?: boolean;
}

export interface ChangeInfo {
  type: 'added' | 'modified' | 'deleted';
  nodePos: number;
  nodeType: string;
  deletedContent?: string; // Content of deleted nodes for ghost rendering
}

// Plugin key for our change indicator plugin
const changeIndicatorPluginKey = new PluginKey('changeIndicator');

// Helper function to convert markdown to a simplified node structure for comparison
function parseMarkdownToNodes(markdown: string): Array<{ type: string; content: string; level?: number }> {
  if (!markdown) return [];

  const nodes: Array<{ type: string; content: string; level?: number }> = [];
  // Use marked.lexer to get block-level tokens
  const tokens = lexer(markdown);

  for (const token of tokens) {
    if (token.type === 'space') continue;

    if (token.type === 'heading') {
      nodes.push({
        type: 'heading',
        content: token.text,
        level: token.depth
      });
    } else if (token.type === 'paragraph') {
      // Check if it's an image-only paragraph (common in markdown)
      // marked parses "![alt](src)" as a paragraph with an image token inside
      if (token.tokens && token.tokens.length === 1 && token.tokens[0].type === 'image') {
        const imgToken = token.tokens[0] as any;
        nodes.push({
          type: 'image',
          content: imgToken.href
        });
      } else {
        nodes.push({
          type: 'paragraph',
          content: token.text
        });
      }
    } else if (token.type === 'image') {
      // Direct image token (less common at top level but possible)
      nodes.push({
        type: 'image',
        content: token.href
      });
    } else if (token.type === 'list') {
      // Treat the entire list as a single block for diffing purposes
      // This avoids desync issues with individual list items
      nodes.push({
        type: 'list',
        content: token.raw
      });
    } else if (token.type === 'table') {
      // Treat the entire table as a single block
      nodes.push({
        type: 'table',
        content: token.raw
      });
    } else if (token.type === 'code') {
      nodes.push({
        type: 'codeBlock',
        content: token.text
      });
    } else if (token.type === 'blockquote') {
      nodes.push({
        type: 'blockquote',
        content: token.text
      });
    } else if (token.type === 'hr') {
      nodes.push({
        type: 'horizontalRule',
        content: '---'
      });
    } else {
      // Fallback for other types (html, etc.)
      nodes.push({
        type: 'paragraph', // Treat as paragraph for simplicity
        content: token.raw || (token as any).text || ''
      });
    }
  }

  return nodes;
}

// Simple diff function to identify changes between two node arrays
function diffNodes(
  oldNodes: Array<{ type: string; content: string; level?: number }>,
  newNodes: Array<{ type: string; content: string; level?: number }>
): ChangeInfo[] {
  const changes: ChangeInfo[] = [];

  // Use content-based matching instead of position-based
  const usedNewIndices = new Set<number>();
  const usedOldIndices = new Set<number>();

  // First pass: find exact matches
  for (let oldIndex = 0; oldIndex < oldNodes.length; oldIndex++) {
    const oldNode = oldNodes[oldIndex];
    for (let newIndex = 0; newIndex < newNodes.length; newIndex++) {
      if (usedNewIndices.has(newIndex)) continue;

      const newNode = newNodes[newIndex];
      if (oldNode.type === newNode.type &&
        oldNode.content === newNode.content &&
        oldNode.level === newNode.level) {
        // Exact match found - no change needed
        usedOldIndices.add(oldIndex);
        usedNewIndices.add(newIndex);
        break;
      }
    }
  }

  // Second pass: find modified content (same type, different content)
  // Use position-based matching with context awareness for better accuracy
  for (let oldIndex = 0; oldIndex < oldNodes.length; oldIndex++) {
    if (usedOldIndices.has(oldIndex)) continue;

    const oldNode = oldNodes[oldIndex];

    // Strategy: Look for a node of the same type in a nearby position
    // This handles cases where content is completely rewritten but structure is preserved
    const searchRadius = 3; // Look within 3 positions
    const startSearch = Math.max(0, oldIndex - searchRadius);
    const endSearch = Math.min(newNodes.length, oldIndex + searchRadius + 1);

    for (let newIndex = startSearch; newIndex < endSearch; newIndex++) {
      if (usedNewIndices.has(newIndex)) continue;

      const newNode = newNodes[newIndex];
      // Match types. For lists/tables/code, we match the type.
      // For paragraphs/headings, we also check level if applicable.
      const typeMatch = oldNode.type === newNode.type;
      const levelMatch = oldNode.level === newNode.level;

      if (typeMatch && levelMatch) {
        // Same type and level, in nearby position = likely a modification
        changes.push({
          type: 'modified',
          nodePos: newIndex,
          nodeType: newNode.type
        });
        usedOldIndices.add(oldIndex);
        usedNewIndices.add(newIndex);
        break;
      }
    }
  }

  // Third pass: find additions
  for (let newIndex = 0; newIndex < newNodes.length; newIndex++) {
    if (usedNewIndices.has(newIndex)) continue;

    const newNode = newNodes[newIndex];
    changes.push({
      type: 'added',
      nodePos: newIndex,
      nodeType: newNode.type
    });
  }

  // Fourth pass: find deletions and determine their positions
  for (let oldIndex = 0; oldIndex < oldNodes.length; oldIndex++) {
    if (usedOldIndices.has(oldIndex)) continue;

    const oldNode = oldNodes[oldIndex];

    // Find the correct position to insert the deletion indicator
    let insertPosition = 0;

    // Look for the next matched node AFTER this deletion in the old document
    let nextMatchedNewIndex = -1;

    for (let i = oldIndex + 1; i < oldNodes.length; i++) {
      if (usedOldIndices.has(i)) {
        // Find the corresponding new node
        const nextOldNode = oldNodes[i];
        for (let j = 0; j < newNodes.length; j++) {
          if (usedNewIndices.has(j)) {
            const newNode = newNodes[j];
            // We need to find the exact match that was established in pass 1 or 2
            // But we don't have the mapping stored explicitly, so we re-verify
            // This is a simplification; ideally we'd store the mapping
            if (nextOldNode.type === newNode.type &&
              nextOldNode.level === newNode.level &&
              (nextOldNode.content === newNode.content || Math.abs(i - j) <= 3)) { // Heuristic
              nextMatchedNewIndex = j;
              break;
            }
          }
        }
        if (nextMatchedNewIndex !== -1) break;
      }
    }

    // If we found a next matched node, insert the deletion BEFORE it
    if (nextMatchedNewIndex !== -1) {
      insertPosition = nextMatchedNewIndex;
    } else {
      // No next matched node found, look for the previous matched node
      for (let i = oldIndex - 1; i >= 0; i--) {
        if (usedOldIndices.has(i)) {
          // Find corresponding new node position
          // Similar heuristic
          for (let j = 0; j < newNodes.length; j++) {
            if (usedNewIndices.has(j)) {
              // We assume this is the match if it's used
              insertPosition = j + 1;
            }
          }
          break;
        }
      }
    }

    changes.push({
      type: 'deleted',
      nodePos: insertPosition,
      nodeType: oldNode.type,
      deletedContent: oldNode.content
    });
  }

  return changes;
}

// Function to create node decorations for change indicators (full-height bars)
function createChangeDecorations(doc: ProseMirrorNode, changes: ChangeInfo[]): DecorationSet {
  const decorations: Decoration[] = [];

  // First, handle deletions by creating ghost elements at their positions
  const deletedChanges = changes.filter(c => c.type === 'deleted');
  deletedChanges.forEach(change => {
    // Find the position to insert the ghost element
    let insertPos = 0;
    let currentNodeIndex = 0;

    doc.descendants((node, pos) => {
      if (!node.isBlock || node.type.name === 'doc') {
        return true;
      }

      if (currentNodeIndex === change.nodePos) {
        insertPos = pos;
        return false; // Stop traversing
      }

      currentNodeIndex++;
      return false; // Stop traversing children, we count top-level blocks
    });

    // If we reached the end and didn't find the pos (e.g. appending at end), use doc size
    if (currentNodeIndex <= change.nodePos) {
      insertPos = doc.content.size;
    }

    // Create a widget decoration for the deleted content
    const ghostElement = document.createElement('div');
    ghostElement.className = 'change-indicator-deleted-placeholder';
    ghostElement.innerHTML = `Content deleted`;

    decorations.push(Decoration.widget(insertPos, ghostElement, {
      side: -1, // Insert before
      marks: []
    }));
  });

  // Then handle existing content changes
  let markdownNodeIndex = 0; // Track position in our markdown-parsed nodes

  /*
  if (changes.length > 0) {
    console.log('🔍 ChangeIndicator Debug:', { changes });
  }
  */

  doc.descendants((node, pos) => {
    // Only process block-level nodes
    if (!node.isBlock || node.type.name === 'doc') {
      return true; // Continue traversing
    }

    /*
    if (changes.length > 0) {
      console.log(`PM Node ${markdownNodeIndex}: ${node.type.name} (Size: ${node.nodeSize})`);
    }
    */

    // Check if this node has changes (excluding deletions which are handled above)
    const change = changes.find(c => c.nodePos === markdownNodeIndex && c.type !== 'deleted');

    if (change) {
      // Verify type match to avoid highlighting wrong nodes due to desync
      // This is a safety check
      let typeMatches = false;
      const nodeType = node.type.name;
      const changeType = change.nodeType;

      if (changeType === 'paragraph' && nodeType === 'paragraph') typeMatches = true;
      else if (changeType === 'heading' && nodeType === 'heading') typeMatches = true;
      else if (changeType === 'image' && nodeType === 'image') typeMatches = true;
      else if (changeType === 'list' && (nodeType === 'bulletList' || nodeType === 'orderedList')) typeMatches = true;
      else if (changeType === 'table' && nodeType === 'table') typeMatches = true;
      else if (changeType === 'codeBlock' && nodeType === 'codeBlock') typeMatches = true;
      else if (changeType === 'blockquote' && nodeType === 'blockquote') typeMatches = true;
      else if (changeType === 'horizontalRule' && nodeType === 'horizontalRule') typeMatches = true;

      // If types don't match perfectly, we might still show it if it's a modification
      // but we should be careful. For now, let's trust the index if it's close.

      decorations.push(Decoration.node(pos, pos + node.nodeSize, {
        class: `change-indicator-wrapper change-indicator-wrapper--${change.type}`,
        'data-tooltip': `${change.type.charAt(0).toUpperCase() + change.type.slice(1)} content`,
      }));
    }

    markdownNodeIndex++;
    return false; // Don't traverse into child nodes (treat lists/tables as single blocks)
  });

  return DecorationSet.create(doc, decorations);
}

export const ChangeIndicator = Extension.create<ChangeIndicatorOptions>({
  name: 'changeIndicator',

  addOptions() {
    return {
      previousContent: '',
      currentContent: '',
      enabled: true,
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: changeIndicatorPluginKey,

        state: {
          init: () => {
            return DecorationSet.empty;
          },

          apply: (tr, oldState, oldEditorState, newEditorState) => {
            // Only update decorations if the document changed or if we have new previous content
            if (!tr.docChanged && !tr.getMeta('updateChangeIndicators')) {
              return oldState.map(tr.mapping, tr.doc);
            }

            const { enabled, previousContent } = this.options;

            if (!enabled || !previousContent) {
              return DecorationSet.empty;
            }

            try {
              // Use directly passed current content instead of extracting from ProseMirror
              const { currentContent } = this.options;
              const currentMarkdown = currentContent || newEditorState.doc.textBetween(0, newEditorState.doc.content.size, '\n');

              // Parse both old and new content
              const oldNodes = parseMarkdownToNodes(previousContent);
              const newNodes = parseMarkdownToNodes(currentMarkdown);

              // Calculate differences
              const changes = diffNodes(oldNodes, newNodes);

              // Create decorations for the changes
              const decorations = createChangeDecorations(newEditorState.doc, changes);

              return decorations;
            } catch (error) {
              console.error('Error creating change decorations:', error);
              return DecorationSet.empty;
            }
          },
        },

        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },

});